<template>
  <component
    :is="disabled ? 'span' : 'button'"
    class="rf-color-tag inline-flex shrink-0 border border-solid border-transparent!"
    :class="{
      'border-dark-blue-1!': props.selected,
      'rounded': !props.pill,
      'rounded-full': props.pill,
      'p-0.5': !disabled,
      [sizeClasses[props.size]]: true,
    }"
    :title="title"
    v-bind="filteredAttrs"
  >
    <span
      class="m-auto inline-flex h-full w-full items-center justify-center gap-1 border border-solid border-transparent"
      :class="{
        'rounded-sm': !props.pill,
        'rounded-full p-1': props.pill,
        'rf-color-tag--pill-container': !!$slots.default || !!props.title,
        'text-beige-1': isColorDarkLocal,
        'text-beige-50': !isColorDarkLocal,
      }"
      :style="{
        'background-color': tagColor,
        'border-color': borderColor,
      }"
    >
      <span class="h-full w-full content-center overflow-hidden text-ellipsis whitespace-nowrap">
        <slot v-if="$slots.default" />
        <template v-else> {{ props.title }} </template>
      </span>
      <slot v-if="$slots.append && !props.disabled" name="append" />
      <button
        v-if="attrs.onClear && props.pill && !props.disabled"
        class="flex items-center justify-center"
        aria-label="Remove"
        @click.stop.prevent="attrs.onClear"
      >
        <RfCloseTagIcon class="stroke-beige-50 h-5! w-5!" />
      </button>
    </span>
  </component>
</template>

<script>
const sizeClasses = {
  small: "rf-color-tag--sm",
  base: "rf-color-tag--base",
};
</script>
<script setup>
import { getTagColor } from "@/utils/constants/ColorsConstants";
import { computed, useAttrs } from "vue";
import RfCloseTagIcon from "@/components/icons/RfCloseTagIcon.vue";
import { isColorDark } from "@/utils/colorHelpers";

const attrs = useAttrs();
defineOptions({ inheritAttrs: false });

const props = defineProps({
  backgroundColor: { type: String, default: "" },
  selected: { type: Boolean, default: false },
  pill: { type: Boolean, default: false },
  title: { type: String, default: "" },
  disabled: { type: Boolean, default: false },
  size: { type: String, default: "base", validator: v => Object.keys(sizeClasses).includes(v) },
});

const tagColor = computed(() => getTagColor(props.backgroundColor));

const borderColor = computed(() => {
  const bgColorUpperCase = props.backgroundColor.toUpperCase();
  if (bgColorUpperCase === "#F2F6F9") return "#EAF0F4";
  if (["#FFF", "#FFFFFF"].includes(bgColorUpperCase)) return "#D8E2EC";
  return "transparent";
});

const filteredAttrs = computed(() =>
  Object.keys(attrs).reduce((acc, key) => {
    if (!(props.disabled && key.startsWith("on"))) acc[key] = attrs[key];
    return acc;
  }, {}),
);

const isColorDarkLocal = computed(() => {
  try {
    if (!tagColor.value) return false;
    return isColorDark(tagColor.value);
  } catch (_e) {
    return false;
  }
});
</script>

<style scoped lang="scss">
.rf-color-tag {
  &--sm {
    font-size: 11px;
    line-height: 14px;
    > .rf-color-tag--pill-container {
      padding-right: calc(var(--spacing) * 2);
      padding-left: calc(var(--spacing) * 2);
    }
  }

  &--base {
    > .rf-color-tag--pill-container {
      padding-right: calc(var(--spacing) * 3);
      padding-left: calc(var(--spacing) * 3);
    }
  }
}
</style>
